import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Input, Tooltip } from 'antd';
import { PlusOutlined, SearchOutlined, EyeOutlined } from '@ant-design/icons';
import { DataTable } from '../../../components/DataTable';
import { usePlanesList } from '../api/usePlanes';
import { useHasPermission } from '../../../auth/useHasPermission';
import { useT } from '../../../locales';
import { formatCurrency, Permission, type PlanResponse } from '@aid/shared';
import { serverSortProps, useServerPaginacion } from '../../../lib/server-sort';

export function PlanesTable() {
  const t = useT();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { page, pageSize, sort, handleChange, setPage } = useServerPaginacion<PlanResponse>(20);

  const { data, isLoading, error } = usePlanesList({
    limit: pageSize,
    offset: (page - 1) * pageSize,
    search: search || undefined,
    ...sort,
  });

  const canCreate = useHasPermission(Permission.PLANES_CREATE);

  if (error) {
    return <div>Error loading planes</div>;
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id' as keyof PlanResponse,
      key: 'id',
      width: 80,
      ...serverSortProps('id', sort),
    },
    {
      title: 'Name',
      dataIndex: 'nombre' as keyof PlanResponse,
      key: 'nombre',
      ...serverSortProps('nombre', sort),
      render: (val: string | null) => val ?? '-',
    },
    {
      title: 'Price',
      dataIndex: 'precio' as keyof PlanResponse,
      key: 'precio',
      ...serverSortProps('precio', sort),
      render: (val: string | null) => (val ? formatCurrency(val) : '-'),
    },
    {
      title: 'Commission',
      dataIndex: 'comision' as keyof PlanResponse,
      key: 'comision',
      ...serverSortProps('comision', sort),
      render: (val: string | null) => (val ? formatCurrency(val) : '-'),
    },
    {
      title: 'Generates Com.',
      dataIndex: 'generaComision' as keyof PlanResponse,
      key: 'generaComision',
      ...serverSortProps('generaComision', sort),
      render: (val: boolean) => (val ? 'Yes' : 'No'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: PlanResponse) => (
        <Tooltip title={t('common.ver')}>
          <Button
            type="text"
            icon={<EyeOutlined />}
            aria-label={t('common.ver')}
            onClick={() => navigate(`/planes/${record.id}`)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <Space wrap size={[8, 8]}>
          <Input
            placeholder="Search planes..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            style={{ width: 250 }}
            allowClear
          />
        </Space>
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/planes/new')}>
            Create Plan
          </Button>
        )}
      </div>
      <DataTable<PlanResponse>
        columns={columns}
        dataSource={data?.data}
        rowKey="id"
        loading={isLoading}
        pagination={{
          current: page,
          pageSize,
          total: data?.total ?? 0,
          showSizeChanger: false,
        }}
        onChange={handleChange}
      />
    </div>
  );
}
